import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import type { DashboardPeriod } from "../utils";

/**
 * Cliente del panel de la home.
 *
 * Es **una sola llamada por carga**, y no una por widget, porque del otro lado el costo no está en
 * calcular el gráfico sino en resolver el alcance de permisos: con un endpoint por widget, un panel
 * de seis widgets lo resolvería seis veces. Ver el change `dashboard-home-dinamico`.
 */

export type WidgetId =
  | "presupuestos"
  | "pedidos"
  | "comparativa"
  | "tasa-conversion"
  | "valor-pipeline"
  | "presupuestos-riesgo"
  | "tiempo-cierre"
  | "nuevos-contactos"
  | "performance-operadores";

/** Widgets del panel que el endpoint todavía no sabe contestar. Pedirlos devuelve 400. */
export const WIDGETS_SIN_DATOS = ["tiempo-respuesta"] as const;

export type EstadoTotal = { count: number; amount: number };
export type PorEstado = { state: string; count: number; amount: number };
export type PorBanda = { band: string; state: string; count: number; amount: number };

export interface DashboardResponse {
  /** Cuándo se recalcularon los agregados. `null` si nunca corrió. */
  calculadoHasta: string | null;
  nuncaCalculado: boolean;
  periodo: { period: DashboardPeriod; desde: string; hasta: string };
  widgets: Partial<{
    "presupuestos": { etapas: PorEstado[]; total: EstadoTotal };
    "pedidos": { estados: PorEstado[]; total: EstadoTotal };
    "comparativa": {
      quotes: { actual: EstadoTotal | null; anterior: EstadoTotal | null };
      orders: { actual: EstadoTotal | null; anterior: EstadoTotal | null };
    };
    "tasa-conversion": { convertidos: number; total: number; tasa: number | null };
    "valor-pipeline": { alcance: "al-dia-de-hoy"; etapas: PorEstado[]; total: EstadoTotal };
    "presupuestos-riesgo": {
      etapa: string;
      medidoPor?: "creacion";
      /** D19: los presupuestos creados entre `desde` y `hasta` (60 días antes del inicio del período). */
      ventana: { desde: string; hasta: string };
      /** Siempre las tres, en orden `7`, `14`, `21`, aunque valgan cero. */
      barras: { band: string; count: number; amount: number }[];
      bandas: PorBanda[];
      total: EstadoTotal;
    };
    "tiempo-cierre": { casos: number; diasPromedio: number | null };
    "nuevos-contactos": { total: number };
    /**
     * Dos formas según el modo, y la diferencia **no es cosmética**: en modo personal la API no
     * manda datos individuales de nadie más. El front no los esconde — no los recibe.
     */
    "performance-operadores": PerformancePersonal | PerformanceSupervisor;
  }>;
}

export interface KpisOperador {
  comprado: number;
  activos: number;
  conversion: number | null;
}

export interface PerformancePersonal {
  modo: "personal";
  /** Cuántos operadores hay en el equipo. Es lo único del resto que se expone. */
  total: number;
  yo: KpisOperador;
  tieneDatosPropios: boolean;
  equipo: { comprado: number | null; activos: number | null; conversion: number | null };
  /** Posición propia, 1 = mejor. `null` si no hay fila propia en el período. */
  ranking: { comprado: number | null; activos: number | null; conversion: number | null };
}

export interface PerformanceSupervisor {
  modo: "supervisor";
  operadores: ({ operatorId: number; nombre: string } & KpisOperador)[];
}

/** Quién puede ver a otros operadores, y a cuáles. Lo consume el selector al montar. */
export interface OperadoresSupervisados {
  canViewOperatorDashboard: boolean;
  operadores: { userId: number; nombre: string; origen: string }[];
}

export async function fetchDashboardOperators(): Promise<OperadoresSupervisados> {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/dashboard/operators`);
  return data;
}

export interface DashboardRequest {
  widgets: WidgetId[];
  period: DashboardPeriod;
  stores: string[];
  operators: number[];
}

export const claveDeRequest = (r: DashboardRequest): string =>
  [
    [...r.widgets].sort().join("+"),
    r.period,
    [...r.stores].sort().join("+"),
    [...r.operators].sort().join("+"),
  ].join("|");

export async function fetchDashboardMetrics(
  r: DashboardRequest,
  signal?: AbortSignal,
): Promise<DashboardResponse> {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/dashboard/metrics`, {
    params: {
      widgets: r.widgets.join(","),
      period: r.period,
      ...(r.stores.length ? { stores: r.stores.join(",") } : {}),
      ...(r.operators.length ? { operators: r.operators.join(",") } : {}),
    },
    signal,
  });
  return data;
}

/**
 * Los stores que aplican **al dashboard**: la unión de los alcances de presupuesto, pedido y
 * contacto.
 *
 * El filtro se alimentaba de `/stores?includeSellerLinked=true`, que contesta otra pregunta —qué
 * stores *administra* el operador— y por eso podía ofrecer stores sin datos y esconder stores con
 * datos.
 */
export async function fetchDashboardStores(): Promise<{
  storeCodes: string[];
  sellerCodes: string[];
}> {
  const { data } = await axiosInstance.get(`${API_BASE_URL}/dashboard/stores`);
  return data;
}
